/**
 * Shared Billing Entity Form
 *
 * Presentational form component for creating and editing billing entities.
 * Used by the billing setup wizard, edit modal, and admin billing section.
 */

import {
  Autocomplete,
  Box,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';

/**
 * Core behavioral health clinician taxonomies
 */
export const TAXONOMY_CODES = [
  { code: '2084P0800X', name: 'Psychiatrist' },
  { code: '2084P0804X', name: 'Child & Adolescent Psychiatrist' },
  { code: '2084P0802X', name: 'Addiction Psychiatrist' },
  { code: '2084A0401X', name: 'Addiction Medicine Psychiatrist' },
  { code: '103T00000X', name: 'Psychologist' },
  { code: '103TC0700X', name: 'Clinical Psychologist' },
  { code: '103TC2200X', name: 'Clinical Child & Adolescent Psychologist' },
  { code: '1041C0700X', name: 'Licensed Clinical Social Worker (LCSW)' },
  { code: '101YP2500X', name: 'Professional Counselor (LPC / LPCC / LCPC)' },
  { code: '101YM0800X', name: 'Mental Health Counselor (LMHC / LCMHC)' },
  { code: '101YA0400X', name: 'Addiction Counselor' },
  { code: '106H00000X', name: 'Marriage & Family Therapist (LMFT)' },
  { code: '103G00000X', name: 'Clinical Neuropsychologist' },
  { code: '363L00000X', name: 'Nurse Practitioner' },
  { code: '363LP0808X', name: 'Psychiatric Nurse Practitioner (PMHNP)' },
  { code: '363A00000X', name: 'Physician Assistant' },
  { code: '364SP0808X', name: 'Psychiatric Clinical Nurse Specialist' },
];

export interface BillingEntityFormData {
  billing_npi: string;
  tax_id: string;
  tax_id_type: string;
  taxonomy_code: string;
  legal_name: string;
  doing_business_as: string;
  billing_address: {
    line1: string;
    line2: string;
    city: string;
    state: string;
    zip: string;
  };
  contact_name: string;
  contact_email: string;
  contact_phone: string;
}

export const initialBillingEntityFormData: BillingEntityFormData = {
  billing_npi: '',
  tax_id: '',
  tax_id_type: 'EIN',
  taxonomy_code: '',
  legal_name: '',
  doing_business_as: '',
  billing_address: {
    line1: '',
    line2: '',
    city: '',
    state: '',
    zip: '',
  },
  contact_name: '',
  contact_email: '',
  contact_phone: '',
};

interface BillingEntityFormProps {
  data: BillingEntityFormData;
  onChange: (field: keyof BillingEntityFormData, value: string) => void;
  onAddressChange: (field: keyof BillingEntityFormData['billing_address'], value: string) => void;
  errors?: Partial<Record<string, string>>;
  /** Disable specific fields (e.g., after Stedi verification) */
  disabledFields?: {
    billing_npi?: boolean;
    tax_id?: boolean;
    tax_id_type?: boolean;
  };
  /** Placeholder text for tax ID field (e.g., masked value in edit mode) */
  taxIdPlaceholder?: string;
  /** Helper text override for tax ID field */
  taxIdHelperText?: string;
  /** Whether tax ID is required (true for create, false for edit) */
  taxIdRequired?: boolean;
}

export function BillingEntityForm({
  data,
  onChange,
  onAddressChange,
  errors = {},
  disabledFields = {},
  taxIdPlaceholder,
  taxIdHelperText,
  taxIdRequired = true,
}: BillingEntityFormProps) {
  return (
    <Grid container spacing={2}>
      {/* NPI */}
      <Grid size={{ xs: 12, sm: 6 }}>
        <TextField
          label="Billing NPI"
          value={data.billing_npi}
          onChange={e => onChange('billing_npi', e.target.value)}
          fullWidth
          required
          disabled={disabledFields.billing_npi}
          error={!!errors.billing_npi}
          helperText={
            errors.billing_npi ||
            (disabledFields.billing_npi
              ? 'Cannot be changed after verification'
              : '10-digit National Provider Identifier')
          }
         slotProps={{ htmlInput: { maxLength: 10 } }}/>
      </Grid>

      {/* Tax ID Type */}
      <Grid size={{ xs: 12, sm: 6 }}>
        <FormControl fullWidth required disabled={disabledFields.tax_id_type}>
          <InputLabel>Tax ID Type</InputLabel>
          <Select
            value={data.tax_id_type}
            label="Tax ID Type"
            onChange={e => onChange('tax_id_type', e.target.value)}
          >
            <MenuItem value="EIN">EIN (Employer Identification Number)</MenuItem>
            <MenuItem value="SSN">SSN (Social Security Number)</MenuItem>
          </Select>
        </FormControl>
      </Grid>

      {/* Tax ID */}
      <Grid size={{ xs: 12 }}>
        <TextField
          label={`Tax ID (${data.tax_id_type})`}
          value={data.tax_id}
          onChange={e => onChange('tax_id', e.target.value)}
          fullWidth
          required={taxIdRequired}
          disabled={disabledFields.tax_id}
          error={!!errors.tax_id}
          helperText={
            errors.tax_id ||
            taxIdHelperText ||
            (disabledFields.tax_id
              ? 'Cannot be changed after verification'
              : 'Format: XX-XXXXXXX (EIN) or XXX-XX-XXXX (SSN)')
          }
          placeholder={taxIdPlaceholder}
        />
      </Grid>

      {/* Taxonomy Code */}
      <Grid size={{ xs: 12 }}>
        <Autocomplete
          value={TAXONOMY_CODES.find(t => t.code === data.taxonomy_code) || null}
          onChange={(_event, newValue) => {
            onChange('taxonomy_code', newValue?.code || '');
          }}
          options={TAXONOMY_CODES}
          getOptionLabel={option => `${option.code} - ${option.name}`}
          renderOption={({ key, ...otherProps }, option) => {
            return (
              <li key={key as string} {...otherProps}>
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {option.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                    {option.code}
                  </Typography>
                </Box>
              </li>
            );
          }}
          renderInput={params => (
            <TextField
              {...params}
              label="Taxonomy Code"
              required
              error={!!errors.taxonomy_code}
              helperText={errors.taxonomy_code || 'Provider specialty/credential type'}
            />
          )}
        />
      </Grid>

      {/* Legal Name */}
      <Grid size={{ xs: 12 }}>
        <TextField
          label="Legal Name"
          value={data.legal_name}
          onChange={e => onChange('legal_name', e.target.value)}
          fullWidth
          required
          error={!!errors.legal_name}
          helperText={errors.legal_name || 'Full legal name of the entity'}
        />
      </Grid>

      {/* Doing Business As (optional) */}
      <Grid size={{ xs: 12 }}>
        <TextField
          label="Doing Business As (DBA)"
          value={data.doing_business_as}
          onChange={e => onChange('doing_business_as', e.target.value)}
          fullWidth
          helperText="Optional - if different from legal name"
        />
      </Grid>

      {/* Address Line 1 */}
      <Grid size={{ xs: 12 }}>
        <TextField
          label="Address Line 1"
          value={data.billing_address.line1}
          onChange={e => onAddressChange('line1', e.target.value)}
          fullWidth
          required
          error={!!errors.billing_address}
        />
      </Grid>

      {/* Address Line 2 */}
      <Grid size={{ xs: 12 }}>
        <TextField
          label="Address Line 2"
          value={data.billing_address.line2}
          onChange={e => onAddressChange('line2', e.target.value)}
          fullWidth
          helperText="Suite, unit, etc. (optional)"
        />
      </Grid>

      {/* City */}
      <Grid size={{ xs: 12, sm: 4 }}>
        <TextField
          label="City"
          value={data.billing_address.city}
          onChange={e => onAddressChange('city', e.target.value)}
          fullWidth
          required
          error={!!errors.billing_address}
        />
      </Grid>

      {/* State */}
      <Grid size={{ xs: 12, sm: 4 }}>
        <TextField
          label="State"
          value={data.billing_address.state}
          onChange={e => onAddressChange('state', e.target.value)}
          fullWidth
          required
          error={!!errors.billing_address}
          helperText="2-letter code (e.g., CA)"
         slotProps={{ htmlInput: { maxLength: 2 } }}/>
      </Grid>

      {/* ZIP */}
      <Grid size={{ xs: 12, sm: 4 }}>
        <TextField
          label="ZIP Code"
          value={data.billing_address.zip}
          onChange={e => onAddressChange('zip', e.target.value)}
          fullWidth
          required
          error={!!errors.billing_address}
          helperText="XXXXX or XXXXX-XXXX"
        />
      </Grid>

      {/* Contact Information */}
      <Grid size={{ xs: 12 }}>
        <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
          Contact Information
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Required for clearinghouse communication about claims
        </Typography>
      </Grid>

      <Grid size={{ xs: 12, sm: 4 }}>
        <TextField
          label="Contact Name"
          value={data.contact_name}
          onChange={e => onChange('contact_name', e.target.value)}
          fullWidth
          helperText="Optional"
        />
      </Grid>

      <Grid size={{ xs: 12, sm: 4 }}>
        <TextField
          label="Contact Email"
          type="email"
          value={data.contact_email}
          onChange={e => onChange('contact_email', e.target.value)}
          fullWidth
          required
          error={!!errors.contact_email}
          helperText={errors.contact_email || 'For claim status notifications'}
        />
      </Grid>

      <Grid size={{ xs: 12, sm: 4 }}>
        <TextField
          label="Contact Phone"
          value={data.contact_phone}
          onChange={e => onChange('contact_phone', e.target.value)}
          fullWidth
          required
          error={!!errors.contact_phone}
          helperText={errors.contact_phone || 'For urgent claim issues'}
        />
      </Grid>
    </Grid>
  );
}
